if (!customElements.get('product-form-popup')) {
  // App-proxy route of the questionnaire app. Override per section with
  // data-app-endpoint if the proxy is mounted somewhere else.
  const DEFAULT_ENDPOINT = '/apps/matat-forms/questionnaire';

  // Answers travel under their `data-key`, except these three: the app names
  // them differently. Anything absent from this map is sent under its own key.
  const ANSWER_KEY_MAP = {
    weight: 'weight_kg',
    health: 'health_status',
    health_followup: 'health_details',
  };

  // data-map values that belong in `contact` rather than in `answers`.
  const CONTACT_KEYS = ['name', 'phone', 'email'];

  // data-map value of the answer that sets the add-to-cart quantity. It is still
  // reported to the app like any other answer.
  const QUANTITY_MAP = 'quantity';

  // Israeli mobile numbers only: 10 digits, always 05X followed by 7 more. A landline
  // prefix (02, 03, 04, 08, 09) or any other length is rejected. The value itself is
  // always these bare digits — 05X-XXXXXXX is a mask drawn over the field, never the
  // value, so this pattern must not expect a separator.
  const IL_MOBILE_LENGTH = 10;
  const IL_MOBILE_PATTERN = /^05\d{8}$/;

  // Shared by the e-mail field's validation and its mask, so an address can never be
  // masked as finished while validation still calls it wrong.
  const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  // The e-mail mask keeps this many characters at each end and covers the rest, one
  // MASK_CHAR per hidden character: dhruvik@gmai.com -> dhXXXXXXXXXXXXom.
  const EMAIL_MASK_KEEP = 2;
  const MASK_CHAR = 'X';

  // Digits and nothing else: no sign, no decimal point, no exponent. Deliberately a
  // pattern over the text rather than Number(), which would accept '1.0', '1e3' and
  // ' 1 ' as integers. The floor is checked separately so that '007' still counts as
  // seven rather than being refused for how it was written.
  const WHOLE_NUMBER_PATTERN = /^\d+$/;
  const WHOLE_NUMBER_MIN = 1;

  // Characters a number input takes from the keyboard that a whole number cannot
  // contain. Refused as they are typed; a paste is still caught by validateStep.
  const NON_INTEGER_KEYS = ['-', '+', '.', ',', 'e', 'E'];

  const toNumber = (value) => {
    const number = Number(value);
    return String(value || '').trim() !== '' && Number.isFinite(number) ? number : null;
  };

  customElements.define(
    'product-form-popup',
    class ProductFormPopup extends HTMLElement {
      connectedCallback() {
        // The popup is authored inside the product info column, which is sticky and
        // can clip or re-parent a fixed element. Move it to <body> so it always
        // covers the viewport. The move re-triggers this callback.
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(`body > product-form-popup[data-section-id="${this.dataset.sectionId}"]`)
            .forEach((stale) => {
              if (stale !== this) stale.remove();
            });
          document.body.appendChild(this);
          return;
        }

        if (this.initialised) return;
        this.initialised = true;

        this.dialog = this.querySelector('.pfp__dialog');
        this.wizard = this.querySelector('[data-pfp-wizard]');
        this.form = this.querySelector('[data-pfp-form]');
        this.steps = Array.from(this.querySelectorAll('[data-pfp-step]'));
        this.progressItems = Array.from(this.querySelectorAll('[data-pfp-progress]'));
        this.stepCountEl = this.querySelector('[data-pfp-step-count]');
        this.nextButton = this.querySelector('[data-pfp-next]');
        this.backButton = this.querySelector('[data-pfp-back]');
        this.spinner = this.querySelector('[data-pfp-spinner]');
        this.formError = this.querySelector('[data-pfp-form-error]');
        this.resultEl = this.querySelector('[data-pfp-result]');
        this.approvedPanel = this.querySelector('[data-pfp-result-approved]');
        this.rejectedPanel = this.querySelector('[data-pfp-result-rejected]');

        this.totalSteps = parseInt(this.dataset.totalSteps, 10) || 1;
        this.productFormId = this.dataset.productFormId;
        this.currentStep = 1;
        this.bypass = false;

        try {
          const skuNode = this.querySelector('[data-pfp-skus]');
          this.variantData = skuNode ? JSON.parse(skuNode.textContent) : {};
        } catch (e) {
          this.variantData = {};
        }

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        // Capture phase so we run before the theme's own product-form submit handler.
        // Both events are watched: click covers the button, submit covers Enter and
        // any script that submits the form directly.
        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('submit', this.onDocumentSubmit, true);
        document.addEventListener('keydown', this.onKeydown);

        this.form.addEventListener('submit', this.onFormSubmit.bind(this));
        if (this.backButton) this.backButton.addEventListener('click', () => this.goToStep(this.currentStep - 1));

        this.querySelectorAll('[data-pfp-close]').forEach((el) => {
          el.addEventListener('click', () => this.close());
        });

        // Before clearFieldError, so the phone number is already in its final shape
        // by the time anything else looks at it.
        this.form.addEventListener('input', this.onFieldInput.bind(this));

        // Clear a field's error as soon as the shopper corrects it.
        this.form.addEventListener('input', this.clearFieldError.bind(this));
        this.form.addEventListener('change', this.clearFieldError.bind(this));

        // A wrong prefix is worth saying as soon as the shopper leaves the field,
        // rather than only when they try to move on.
        this.form.addEventListener('focusout', this.onFieldBlur.bind(this));

        // Three ways back into a masked field, all of which must lift the mask off the
        // value: tabbing or clicking in from outside (focusin), clicking a field that
        // already has focus (pointerdown, which focusin would not fire for), and simply
        // carrying on typing (keydown).
        const reveal = this.revealValue.bind(this);
        this.form.addEventListener('focusin', reveal);
        this.form.addEventListener('pointerdown', reveal);
        this.form.addEventListener('keydown', reveal);

        this.form.addEventListener('keydown', ProductFormPopup.blockNonInteger);

        this.updateConditionalFields();
        this.refreshMasks();
        this.goToStep(1);

        if (!this.productForm) {
          console.warn(
            `[product-form-popup] ready, but no form with id "${this.productFormId}" is on the page — ` +
              'Add to cart cannot be intercepted. Check that the Buy buttons block is present.'
          );
        } else {
          console.info(`[product-form-popup] ready — intercepting #${this.productFormId} (${this.totalSteps} step(s))`);
        }
      }

      disconnectedCallback() {
        // Ignore the disconnect caused by moving ourselves into <body>.
        if (!this.initialised) return;
        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('submit', this.onDocumentSubmit, true);
        document.removeEventListener('keydown', this.onKeydown);
        this.unlockScroll();
      }

      /* ---------------------------------------------------------------- *
       * Opening / closing
       * ---------------------------------------------------------------- */

      get productForm() {
        return document.getElementById(this.productFormId);
      }

      /** True when the element is the add-to-cart control of the form we guard. */
      isGuardedTrigger(node) {
        if (!node || typeof node.closest !== 'function') return false;

        const button = node.closest('[type="submit"][name="add"], .product-form__submit');
        if (!button) return false;

        // button.form resolves through the `form` attribute too, unlike closest('form').
        const form = button.form || button.closest('form');

        // Read the id ATTRIBUTE, not the .id property: Shopify's product form carries a
        // hidden <input name="id"> for the variant id, and a named form control shadows
        // the form's own .id property with itself — form.id becomes that <input>, not a string.
        const formId = form ? form.getAttribute('id') : null;

        if (!form || formId !== this.productFormId) {
          console.warn(
            '[product-form-popup] add-to-cart clicked but the form does not match. ' +
              `Button's form: "${formId || '(none)'}" — guarding: "${this.productFormId}". ` +
              'The popup will not open for this button.'
          );
          return false;
        }

        return true;
      }

      onDocumentClick(event) {
        if (this.bypass) return;
        if (!this.isGuardedTrigger(event.target)) return;

        event.preventDefault();
        event.stopPropagation();
        this.open();
      }

      onDocumentSubmit(event) {
        if (this.bypass) return;

        const form = event.target;
        // getAttribute, not .id — see the comment in isGuardedTrigger for why.
        if (!form || form.getAttribute('id') !== this.productFormId) return;

        event.preventDefault();
        event.stopPropagation();
        this.open();
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
      }

      open() {
        this.removeAttribute('hidden');
        this.setAttribute('open', '');
        this.lockScroll();

        this.resultEl.hidden = true;
        this.wizard.hidden = false;
        this.dialog.classList.remove('pfp__dialog--compact');
        this.goToStep(1);

        // Answers survive a close, so the errors they were marked with must not: a
        // half-typed phone number flagged on the way out would otherwise greet the
        // shopper in red the next time they open the questionnaire.
        this.allFields.forEach((field) => field.classList.remove('has-error'));
        this.refreshMasks();

        this.dialog.focus();
      }

      close() {
        this.removeAttribute('open');
        this.setAttribute('hidden', '');
        this.unlockScroll();

        const trigger = this.productForm && this.productForm.querySelector('[type="submit"][name="add"]');
        if (trigger) trigger.focus();
      }

      lockScroll() {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }

      unlockScroll() {
        document.body.style.overflow = this.previousOverflow || '';
      }

      /* ---------------------------------------------------------------- *
       * Navigation
       * ---------------------------------------------------------------- */

      goToStep(step) {
        const target = Math.min(Math.max(step, 1), this.totalSteps);
        this.currentStep = target;

        this.steps.forEach((el) => {
          el.hidden = parseInt(el.dataset.pfpStep, 10) !== target;
        });

        this.progressItems.forEach((el) => {
          el.classList.toggle('is-done', parseInt(el.dataset.pfpProgress, 10) <= target);
        });

        if (this.stepCountEl) {
          const format = this.dataset.stepCountFormat || '';
          this.stepCountEl.textContent = format
            .replace('[current]', target)
            .replace('[total]', this.totalSteps);
        }

        if (this.backButton) this.backButton.hidden = target === 1;
        if (this.nextButton) {
          this.nextButton.textContent =
            target === this.totalSteps ? this.nextButton.dataset.submitLabel || this.nextButton.textContent : this.nextButton.dataset.nextLabel || this.nextButton.textContent;
        }

        this.hideFormError();
        this.dialog.scrollTop = 0;
      }

      /* ---------------------------------------------------------------- *
       * Validation
       * ---------------------------------------------------------------- */

      fieldsForStep(step) {
        const stepEl = this.steps.find((el) => parseInt(el.dataset.pfpStep, 10) === step);
        return stepEl ? Array.from(stepEl.querySelectorAll('[data-pfp-field]:not([hidden])')) : [];
      }

      get allFields() {
        return Array.from(this.querySelectorAll('[data-pfp-field]'));
      }

      get visibleFields() {
        return Array.from(this.querySelectorAll('[data-pfp-field]:not([hidden])'));
      }

      /**
       * Reveals a follow-up field exactly when the question it depends on is
       * answered with something other than that question's own approved answer —
       * e.g. the "describe your pet's condition" field appears only once the
       * health question is answered with anything but the approved choice.
       * The approved answer lives once, on the source field's own `data-approve`,
       * so there is nothing to keep in sync between the two fields.
       */
      updateConditionalFields() {
        this.allFields.forEach((field) => {
          const sourceKey = field.dataset.visibleUnlessApprovedKey;
          if (!sourceKey) return;

          const source = this.querySelector(`[data-key="${sourceKey}"]`);
          if (!source) return;

          const value = this.readField(source);
          const answered = Array.isArray(value) ? value.length > 0 : value !== '';
          const visible = answered && !ProductFormPopup.isApproved(source.dataset.approve, value);

          field.hidden = !visible;
          if (!visible) field.classList.remove('has-error');
        });
      }

      readField(field) {
        const type = field.dataset.type;

        if (type === 'checkbox') {
          return Array.from(field.querySelectorAll('input:checked')).map((input) => input.value);
        }

        if (type === 'radio') {
          const checked = field.querySelector('input:checked');
          return checked ? checked.value : '';
        }

        const control = field.querySelector('input, textarea, select');
        return control ? control.value.trim() : '';
      }

      static isEmpty(value) {
        return Array.isArray(value) ? value.length === 0 : value === '';
      }

      /**
       * How many units of a variant may still be added: the stock left once the
       * cart is accounted for, capped by the variant's own per-order maximum.
       * Untracked stock and an 'allow overselling' policy both mean no ceiling.
       */
      static variantLimit(data) {
        const inCart = data.inCart || 0;
        let limit = Infinity;

        if (data.tracked) limit = (data.inventory || 0) - inCart;
        // A quantity rule is enforced on the whole cart line, so the cart counts here too.
        if (data.max) limit = Math.min(limit, data.max - inCart);

        return Math.max(0, limit);
      }

      /** The value as a cart quantity — a whole number of 1 or more — or null. */
      static toQuantity(value) {
        const number = Number(value);
        return Number.isInteger(number) && number >= 1 ? number : null;
      }

      /**
       * The value the phone field should hold: nothing but the digits of a local
       * Israeli number, capped at ten. Everything else is dropped, so a number pasted
       * as +972…, 00972…, or with spaces, dots or dashes in it all come down to the
       * same ten digits. The 05X-XXXXXXX pattern is never written into the value —
       * that is the mask's job, and the mask is only ever a display.
       */
      static normalisePhone(value) {
        return String(value == null ? '' : value)
          .replace(/\D/g, '')
          .replace(/^(?:00)?972/, '0')
          .slice(0, IL_MOBILE_LENGTH);
      }

      /** Digits only, for a value that reached us without passing through the field. */
      static unformatPhone(value) {
        return String(value == null ? '' : value).replace(/\D/g, '');
      }

      /**
       * An address with all but its first and last few characters covered, one mask
       * character per hidden one: dhruvik@gmai.com -> dhXXXXXXXXXXXXom.
       *
       * Anything too short to keep both ends and still hide something is covered
       * whole, so the mask can never end up showing the entire address.
       */
      static maskEmail(value) {
        const text = String(value == null ? '' : value);
        const hidden = text.length - EMAIL_MASK_KEEP * 2;

        if (hidden < 1) return MASK_CHAR.repeat(text.length);

        return (
          text.slice(0, EMAIL_MASK_KEEP) + MASK_CHAR.repeat(hidden) + text.slice(-EMAIL_MASK_KEEP)
        );
      }

      /**
       * Keeps a minus sign, a decimal point or an exponent out of a number question as
       * it is typed. A number input accepts all of them from the keyboard, which would
       * otherwise let a shopper build a value the question cannot take and only find out
       * when they press Next.
       */
      static blockNonInteger(event) {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (NON_INTEGER_KEYS.indexOf(event.key) === -1) return;

        const field = ProductFormPopup.fieldOf(event.target);
        if (field && field.dataset.type === 'number') event.preventDefault();
      }

      /** The questionnaire field an event came from, or null if it came from elsewhere. */
      static fieldOf(node) {
        if (!node || typeof node.closest !== 'function') return null;
        return node.closest('[data-pfp-field]');
      }

      /**
       * True when the field holds a finished value — the point at which its mask is
       * allowed to cover it. A half-typed number or a malformed address is left
       * showing, so the shopper can see what still needs fixing.
       */
      isComplete(field, value) {
        if (field.dataset.type === 'tel') return IL_MOBILE_PATTERN.test(value);
        if (field.dataset.type === 'email') return EMAIL_PATTERN.test(value);
        return false;
      }

      /** Draws the field's mask over it, or takes it away. Fields without one are skipped. */
      updateMask(field) {
        const mask = field.querySelector('[data-pfp-mask]');
        if (!mask) return;

        const value = this.readField(field);
        const complete = this.isComplete(field, value);

        // The phone mask is a fixed pattern the merchant authored, so it is left as
        // rendered; the e-mail mask is derived from the address and has to be rebuilt
        // whenever that changes.
        if (complete && field.dataset.type === 'email') {
          mask.textContent = ProductFormPopup.maskEmail(value);
        }

        mask.hidden = !complete;
      }

      /** Applies the rule above to every masked field, for state we did not observe. */
      refreshMasks() {
        this.allFields.forEach((field) => this.updateMask(field));
      }

      /**
       * The dash is ours, not the shopper's, so deleting it on its own would be a dead
       * keystroke — the formatter would put it straight back. When a Backspace or Delete
       * removed nothing but the separator, this takes the digit beside it instead, in the
       * Keeps the phone field down to its ten digits, and draws either mask over its
       * field on the keystroke that completes the value.
       */
      onFieldInput(event) {
        const input = event.target;
        const field = ProductFormPopup.fieldOf(input);
        if (!field) return;

        // Rewriting the value mid-composition would corrupt what the IME is still
        // assembling; the input event that follows compositionend normalises it.
        if (event.isComposing) return;

        if (field.dataset.type === 'tel') {
          const normalised = ProductFormPopup.normalisePhone(input.value);

          if (normalised !== input.value) {
            // Count the digits before the caret, then put the caret back after that same
            // digit — otherwise dropping a stray character would throw the caret to the
            // end and editing the middle of the number would be impossible.
            const digitsBefore = input.value.slice(0, input.selectionStart || 0).replace(/\D/g, '').length;
            input.value = normalised;
            input.setSelectionRange(digitsBefore, digitsBefore);
          }
        }

        // The character that completes the value is what draws the mask, so this runs on
        // every keystroke, including the ones that changed nothing about the value.
        this.updateMask(field);
      }

      /** '' when the phone number is a valid Israeli mobile, otherwise the message. */
      phoneMessage(field, value) {
        return IL_MOBILE_PATTERN.test(value) ? '' : field.dataset.phoneError || '';
      }

      /**
       * Any return to the field lifts its mask, so what is underneath is never out of
       * reach. Focus alone is not enough to hang this on: the mask is drawn on the
       * keystroke that completes the value, at which point the field already has focus,
       * and clicking an already-focused field fires no focus event at all.
       */
      revealValue(event) {
        const field = ProductFormPopup.fieldOf(event.target);
        if (!field) return;

        const mask = field.querySelector('[data-pfp-mask]');
        if (mask) mask.hidden = true;
      }

      /**
       * Editing ends: the mask comes back over a finished value, and a bad phone prefix
       * is flagged here rather than only when the shopper tries to move on.
       */
      onFieldBlur(event) {
        const field = ProductFormPopup.fieldOf(event.target);
        if (!field) return;

        this.updateMask(field);

        if (field.dataset.type !== 'tel') return;

        const value = this.readField(field);
        if (!value || IL_MOBILE_PATTERN.test(value)) return;

        field.classList.add('has-error');
        const errorEl = field.querySelector('[data-pfp-field-error]');
        // Always written, even when blank: the paragraph is rendered pre-filled with the
        // 'required' message, which would otherwise be shown to a shopper who did fill
        // the field in — see the same rule in validateStep.
        if (errorEl) errorEl.textContent = this.phoneMessage(field, value);
      }

      validateStep(step) {
        let firstInvalid = null;

        this.fieldsForStep(step).forEach((field) => {
          const value = this.readField(field);
          const required = field.dataset.required === 'true';
          let invalid = false;
          let message = '';

          if (required && ProductFormPopup.isEmpty(value)) {
            invalid = true;
            message = field.dataset.requiredError || '';
          } else if (field.dataset.type === 'email' && value && !EMAIL_PATTERN.test(value)) {
            invalid = true;
            message = field.dataset.emailError || '';
          } else if (field.dataset.type === 'tel' && value) {
            message = this.phoneMessage(field, value);
            invalid = !IL_MOBILE_PATTERN.test(value);
          } else if (field.dataset.map === QUANTITY_MAP && value) {
            // Judged on the number, never on whether a message happens to be configured —
            // a merchant who clears one of the quantity error settings must not thereby
            // switch the stock guard off.
            invalid = this.quantityInvalid(value);
            message = invalid ? this.quantityMessage(field, value) : '';
          } else if (field.dataset.type === 'number' && value) {
            // Every number question except the quantity, which is judged against stock
            // just above: weight and age are whole numbers of 1 or more, so a decimal, a
            // negative or a zero is refused however it got into the field.
            invalid = !WHOLE_NUMBER_PATTERN.test(value) || Number(value) < WHOLE_NUMBER_MIN;
            message = invalid ? field.dataset.wholeNumberError || '' : '';
          }

          if (invalid) {
            field.classList.add('has-error');
            const errorEl = field.querySelector('[data-pfp-field-error]');
            // Written even when the merchant left the message blank: the paragraph is
            // rendered pre-filled with the 'required' message, and showing that on a
            // field the shopper did fill in would be a lie they cannot act on.
            if (errorEl) errorEl.textContent = message;
            if (!firstInvalid) firstInvalid = field;
          } else {
            field.classList.remove('has-error');
          }
        });

        if (firstInvalid) {
          const control = firstInvalid.querySelector('input, textarea, select');
          if (control) control.focus();
          return false;
        }

        return true;
      }

      /** True when the value is not a quantity this variant can currently take. */
      quantityInvalid(value) {
        const quantity = ProductFormPopup.toQuantity(value);
        return !quantity || quantity > this.currentVariant().limit;
      }

      /**
       * '' when the value is a quantity this variant can actually take, otherwise the
       * message to show. Anything /cart/add would reject has to be caught here: the
       * theme flips its own add-to-cart button to 'sold out' and disables it on a
       * failed add, so a shopper who got that far could not retry even after fixing
       * the number — they would just be told they are approved with an empty cart.
       */
      quantityMessage(field, value) {
        const quantity = ProductFormPopup.toQuantity(value);
        if (!quantity) return field.dataset.quantityError || '';

        const limit = this.currentVariant().limit;
        if (quantity <= limit) return '';

        // A ceiling the shopper can work with reads better as a number than as a
        // flat refusal, so [count] carries what is still addable.
        if (limit < 1) return field.dataset.noStockError || '';
        return (field.dataset.stockError || '').replace('[count]', limit);
      }

      clearFieldError(event) {
        const field = event.target.closest('[data-pfp-field]');
        if (field) field.classList.remove('has-error');
        this.updateConditionalFields();
      }

      /* ---------------------------------------------------------------- *
       * Eligibility
       * ---------------------------------------------------------------- */

      /** A blank approve list always passes; otherwise every selected answer must be listed. */
      static isApproved(rawApprove, value) {
        const approve = (rawApprove || '').trim();
        if (!approve) return true;

        const allowed = approve
          .split(',')
          .map((entry) => entry.trim().toLowerCase())
          .filter(Boolean);

        const selected = (Array.isArray(value) ? value : [value])
          .map((entry) => String(entry).trim().toLowerCase())
          .filter(Boolean);

        if (!selected.length) return false;

        return selected.every((entry) => allowed.indexOf(entry) !== -1);
      }

      collectAnswers() {
        // Hidden conditional questions are neither reported nor scored.
        return this.visibleFields.map((field) => {
          const raw = this.readField(field);
          // The dash in the phone field is there to be read, not to be stored — the
          // app and the cart line get the ten plain digits.
          const value = field.dataset.type === 'tel' ? ProductFormPopup.unformatPhone(raw) : raw;
          return {
            key: field.dataset.key || '',
            label: field.dataset.label || '',
            map: field.dataset.map || 'none',
            approve: field.dataset.approve || '',
            type: field.dataset.type || 'text',
            value,
            display: Array.isArray(value) ? value.join(', ') : value,
          };
        });
      }

      /* ---------------------------------------------------------------- *
       * Submission
       * ---------------------------------------------------------------- */

      onFormSubmit(event) {
        event.preventDefault();

        if (!this.validateStep(this.currentStep)) return;

        if (this.currentStep < this.totalSteps) {
          this.goToStep(this.currentStep + 1);
          return;
        }

        this.finish();
      }

      async finish() {
        const answers = this.collectAnswers();
        const approved = answers.every((answer) => ProductFormPopup.isApproved(answer.approve, answer.value));
        const status = approved ? this.dataset.approvedStatus : this.dataset.rejectedStatus;

        this.setBusy(true);
        this.hideFormError();

        try {
          await this.sendToApp(answers, status);
        } catch (error) {
          console.error('[product-form-popup] questionnaire submission failed', error);
          // The shopper should not be blocked by a delivery failure — carry on.
        }

        if (approved && this.dataset.addToCartOnApproval === 'true') {
          this.applyQuantity();
          this.attachLineItemProperties(answers, status);
          // When we show our own approval dialog, the theme's cart drawer would
          // otherwise slide open behind it.
          if (this.dataset.closeOnApproval !== 'true') this.suppressCartDrawer();
          this.addToCart();
        }

        this.setBusy(false);
        this.showResult(approved);
      }

      setBusy(busy) {
        if (this.spinner) this.spinner.hidden = !busy;
        if (this.nextButton) this.nextButton.setAttribute('aria-disabled', busy ? 'true' : 'false');
      }

      currentVariant() {
        const form = this.productForm;
        const input = form && form.querySelector('[name="id"]');
        const id = input ? input.value : '';
        const data = this.variantData[id] || {};
        return {
          id,
          sku: data.sku || this.dataset.productSku || '',
          title: data.title || '',
          limit: ProductFormPopup.variantLimit(data),
        };
      }

      get endpoint() {
        return this.dataset.appEndpoint || DEFAULT_ENDPOINT;
      }

      /** Shapes the questionnaire the way the app expects it. */
      buildPayload(answers, status) {
        const variant = this.currentVariant();
        const contact = {};
        const collected = {};

        answers.forEach((answer) => {
          // Multi-answer questions are flattened, so every value is a string.
          if (CONTACT_KEYS.indexOf(answer.map) !== -1) {
            if (answer.display !== '') contact[answer.map] = answer.display;
            return;
          }

          const key = ANSWER_KEY_MAP[answer.key] || answer.key;
          if (key) collected[key] = answer.display;
        });

        return {
          status,
          productId: toNumber(this.dataset.productId),
          variantId: toNumber(variant.id),
          sku: variant.sku,
          productHandle: this.dataset.productHandle || '',
          productTitle: this.dataset.productTitle || '',
          variantTitle: variant.title === 'Default Title' ? '' : variant.title,
          contact,
          answers: collected,
        };
      }

      async sendToApp(answers, status) {
        const payload = this.buildPayload(answers, status);

        if (!payload.contact.email) {
          console.warn('[product-form-popup] no answer is mapped to "E-mail" — submitting without one.');
        }

        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`${this.endpoint} responded with ${response.status}`);

        // The proxy always answers with JSON. Anything else means Shopify served
        // a normal page instead — i.e. the app proxy prefix is not set up.
        let result;
        try {
          result = await response.json();
        } catch (error) {
          throw new Error(`${this.endpoint} did not return JSON — check the app proxy configuration`);
        }

        if (result && result.ok === false) {
          throw new Error(`${this.endpoint} rejected the questionnaire: ${result.error || 'unknown error'}`);
        }

        return result;
      }

      get quantityField() {
        return this.querySelector(`[data-pfp-field][data-map="${QUANTITY_MAP}"]`);
      }

      /**
       * Copies the quantity the shopper asked for onto the form that adds to cart,
       * so one questionnaire adds one line of N units.
       *
       * The theme's quantity input sits OUTSIDE that <form> and is tied to it with a
       * `form` attribute, so it has to be reached through form.elements — a
       * querySelector on the form would never see it. Sections without a quantity
       * selector block have no such input at all, so a hidden one carries the value.
       */
      applyQuantity() {
        const form = this.productForm;
        const field = this.quantityField;
        if (!form || !field) return;

        const quantity = ProductFormPopup.toQuantity(this.readField(field));
        if (!quantity) return;

        // Ours goes first, so a leftover from an earlier submit cannot end up as a
        // second `quantity` entry in the posted form data.
        form.querySelectorAll('[data-pfp-quantity]').forEach((el) => el.remove());

        const inputs = Array.from(form.elements).filter((el) => el.name === 'quantity');

        if (inputs.length) {
          inputs.forEach((input) => {
            input.value = quantity;
            // Keeps quantity-input's own +/- buttons in step with the new value.
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          return;
        }

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'quantity';
        hidden.value = quantity;
        hidden.setAttribute('data-pfp-quantity', '');
        form.appendChild(hidden);
      }

      /**
       * Persist the answers on the cart line so they follow the order through
       * to fulfilment. Only runs on the approved path, since a rejected
       * questionnaire never reaches the cart.
       */
      attachLineItemProperties(answers, status) {
        const form = this.productForm;
        if (!form || this.dataset.saveToLineItem !== 'true') return;

        form.querySelectorAll('[data-pfp-property]').forEach((el) => el.remove());

        const prefix = this.dataset.hideLineItemProperties === 'true' ? '_' : '';
        const variant = this.currentVariant();

        const append = (label, value) => {
          if (!label || value === '' || value == null) return;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${prefix}${label}]`;
          input.value = value;
          input.setAttribute('data-pfp-property', '');
          form.appendChild(input);
        };

        append(this.dataset.statusFieldLabel, status);
        append(this.dataset.skuFieldLabel, variant.sku);
        answers.forEach((answer) => {
          // applyQuantity already put this one on the line as its real quantity.
          if (answer.map === QUANTITY_MAP) return;
          append(answer.label, answer.display);
        });
      }

      /**
       * Temporarily shadows CartDrawer.open so the cart still re-renders (header
       * count, line items) but does not slide over our approval dialog.
       */
      suppressCartDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.open !== 'function') return;

        drawer.open = function noop() {};
        setTimeout(() => {
          delete drawer.open;
        }, 3000);
      }

      addToCart() {
        const form = this.productForm;
        if (!form) return;

        const button = form.querySelector('[type="submit"][name="add"]');

        this.bypass = true;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(button || undefined);
        } else if (button) {
          button.click();
        } else {
          form.submit();
        }
        // Release on the next tick, once the synchronous submit has gone through.
        setTimeout(() => {
          this.bypass = false;
        }, 0);
      }

      showResult(approved) {
        if (approved && this.dataset.closeOnApproval === 'true') {
          this.close();
          return;
        }

        this.wizard.hidden = true;
        this.resultEl.hidden = false;
        this.approvedPanel.hidden = !approved;
        this.rejectedPanel.hidden = approved;
        this.dialog.classList.add('pfp__dialog--compact');
        this.dialog.scrollTop = 0;
        this.dialog.focus();
      }

      showFormError(message) {
        if (!this.formError) return;
        this.formError.textContent = message;
        this.formError.hidden = false;
      }

      hideFormError() {
        if (this.formError) this.formError.hidden = true;
      }
    }
  );
}